import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  normalizeRequestedWaterIntelCapabilities,
  PHASE_A_DEFAULT_WATER_INTEL_CAPABILITIES,
} from './waterIntelCapabilities';
import {
  readWaterIntelClientCache,
  waterIntelClientCacheKey,
  writeWaterIntelClientCache,
} from './waterIntelCache';
import type {
  GetWaterIntelReadInput,
  WaterIntelRead,
} from './waterIntelTypes';

type WaterIntelClientOptions = {
  /**
   * Caller-specific wait budget. The direct Water Intel card keeps the default
   * 12s budget; background FishIQ fusion can use a smaller quiet budget.
   */
  timeoutMs?: number;
  /** Expected background misses should fail soft without polluting DevTools. */
  quiet?: boolean;
};

const inflight = new Map<string, Promise<WaterIntelRead | null>>();

function asFiniteNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Water Intelligence timeout after ${ms}ms`)), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function normalizedPoint(input: GetWaterIntelReadInput): {
  lat: number;
  lng: number;
  cacheKey: string;
} | null {
  const lat = asFiniteNumber(input.lat);
  const lng = asFiniteNumber(input.lng ?? input.lon);
  if (lat == null || lng == null || lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng, cacheKey: waterIntelClientCacheKey(lat, lng) };
}

/**
 * Synchronous cache peek used by passive/boot FishIQ hydration.
 * Never performs auth, networking, or a callable invocation.
 */
export function getCachedWaterIntelRead(
  input: GetWaterIntelReadInput,
): WaterIntelRead | null {
  const point = normalizedPoint(input);
  if (!point) return null;
  return readWaterIntelClientCache(point.cacheKey);
}

export async function getWaterIntelRead(
  input: GetWaterIntelReadInput,
  options: WaterIntelClientOptions = {},
): Promise<WaterIntelRead | null> {
  const point = normalizedPoint(input);
  if (!point) return null;

  const { lat, lng, cacheKey } = point;
  const cached = readWaterIntelClientCache(cacheKey);
  if (cached) return cached;

  // One exact-anchor request owns the callable while it is in flight. This is
  // shared by Water Intel + FishIQ so opening both cannot double-hit Functions.
  if (inflight.has(cacheKey)) return inflight.get(cacheKey)!;

  const timeoutMs = Math.max(1500, Math.min(20000, Math.floor(Number(options.timeoutMs) || 12000)));

  const task = (async () => {
    try {
      const user = getAuth().currentUser;
      if (!user?.uid) return null;
      await user.getIdToken(false);

      const requestedCapabilities = normalizeRequestedWaterIntelCapabilities(
        input.requestedCapabilities?.length
          ? input.requestedCapabilities
          : PHASE_A_DEFAULT_WATER_INTEL_CAPABILITIES,
      );

      const callable = httpsCallable<GetWaterIntelReadInput, WaterIntelRead>(
        functions,
        'getWaterIntelRead',
      );

      const result = await withTimeout(
        callable({ lat, lng, requestedCapabilities }) as Promise<any>,
        timeoutMs,
      );
      const value = (result as any)?.data as WaterIntelRead | undefined;
      if (!value || value.schemaVersion !== 1) return null;

      writeWaterIntelClientCache(cacheKey, value);
      return value;
    } catch (error) {
      if (!options.quiet) console.warn('[waterIntel] soft fail', error);
      return null;
    } finally {
      inflight.delete(cacheKey);
    }
  })();

  inflight.set(cacheKey, task);
  return task;
}
